import { AccordionBodyItem } from 'component/Accordion';
import { RadioButton } from 'component/RadioButton';
import { SelectComponent } from 'component/Select';
import { React, useEffect, useState } from 'react';
import { getLocalCommunities, getSettlement } from 'api/LocationAPI';
import { buildSettlementValue, mapLocationOptions, mapSettlementOptions } from './locationOptions';

const emptyNearestSettlement = {
  matches: false,
  local_community: '',
  settlement_name: '',
  settlement_code: '',
};

export const ClosestSettlementV2 = (props) => {
  const location = props.formData.info.location;
  const nearest = location.nearest_settlement_name ?? emptyNearestSettlement;
  const { region, district } = location;

  const [communityOptions, setCommunityOptions] = useState([]);
  const [settlementOptions, setSettlementOptions] = useState([]);
  const [selectsVisible, setSelectsVisible] = useState(!nearest.matches);

  const updateNearestFields = (fields) => {
    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        location: {
          ...prevFormData.info.location,
          nearest_settlement_name: {
            ...emptyNearestSettlement,
            ...prevFormData.info.location.nearest_settlement_name,
            ...fields,
          },
        },
      },
    }));
  };

  const clearClosestSettlementSelection = () => {
    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        location: {
          ...prevFormData.info.location,
          nearest_settlement_name: {
            ...prevFormData.info.location.nearest_settlement_name,
            local_community: '',
            settlement_name: '',
            settlement_code: '',
          },
        },
      },
    }));
  };

  useEffect(() => {
    if (!region || !district) {
      setCommunityOptions([]);
      return;
    }

    const loadCommunities = async () => {
      try {
        const response = await getLocalCommunities(region, district);
        setCommunityOptions(mapLocationOptions(response?.data));
      } catch (error) {
        console.error('Error loading closest settlement communities:', error);
        setCommunityOptions([]);
      }
    };

    loadCommunities();
  }, [region, district]);

  useEffect(() => {
    if (!region || !district || !nearest.local_community) {
      setSettlementOptions([]);
      return;
    }

    const loadSettlements = async () => {
      try {
        const response = await getSettlement(region, district, nearest.local_community);
        setSettlementOptions(mapSettlementOptions(response?.data));
      } catch (error) {
        console.error('Error loading closest settlements:', error);
        setSettlementOptions([]);
      }
    };

    loadSettlements();
  }, [region, district, nearest.local_community]);

  const handleLocationMatch = (value) => {
    const matches = value === 'true' || value === true;
    setSelectsVisible(!matches);

    if (matches) {
      clearClosestSettlementSelection();
    }

    props.setFormData((prevFormData) => ({
      ...prevFormData,
      info: {
        ...prevFormData.info,
        location: {
          ...prevFormData.info.location,
          nearest_settlement_name: {
            ...emptyNearestSettlement,
            ...prevFormData.info.location.nearest_settlement_name,
            matches,
            ...(matches
              ? {
                local_community: '',
                settlement_name: '',
                settlement_code: '',
              }
              : {}),
          },
        },
      },
    }));
  };

  const handleCommunity = (value) => {
    updateNearestFields({
      local_community: value,
      settlement_name: '',
      settlement_code: '',
    });
  };

  const handleSettlement = (code, name) => {
    updateNearestFields({ settlement_name: name, settlement_code: code });
  };

  const locationReady = Boolean(region && district);

  return (
    <AccordionBodyItem
      accordionSubId="2.6"
      titleUA="Найближчий населений пункт"
      titleEN="Closest settlement"
      children={(
        <div className="flex-column-group text-left flex-wrap">
          <RadioButton
            id="closest-matches"
            name="closest-match"
            value
            handleInput={handleLocationMatch}
            checked={nearest.matches}
            disabled={false}
            label="Співпадає з 2.5 \ Matches 2.5"
          />
          <RadioButton
            id="closest-no-matches"
            name="closest-match"
            value={false}
            handleInput={handleLocationMatch}
            checked={!nearest.matches}
            disabled={false}
            label={(
              <>
                <div>Не співпадає з 2.5 (вказати нижче ТГ, населений пункт) /</div>
                <div>Doesn’t match 2.5 (specify below council, settlement)</div>
              </>
            )}
          />

          {selectsVisible && (
            <>
              {!locationReady && (
                <div className="error-text">
                  Спочатку оберіть область (2.1) та район (2.2) / Select region (2.1) and district (2.2) first
                </div>
              )}
              <SelectComponent
                options={communityOptions}
                placeholder="Local community"
                handleSelect={handleCommunity}
                value={nearest.local_community
                  ? { label: nearest.local_community, value: nearest.local_community }
                  : null}
                isDisabled={!locationReady}
              />
              <SelectComponent
                options={settlementOptions}
                placeholder="Settlement name"
                handleSelect={handleSettlement}
                value={buildSettlementValue(nearest.settlement_name, nearest.settlement_code)}
                isDisabled={!locationReady || !nearest.local_community}
              />
              {nearest.settlement_code && (
                <div>
                  <div>Код нас. пункту / Settlement ID: {nearest.settlement_code}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    />
  );
};
